import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Camera, Loader2, X, Check } from "lucide-react";
import { uploadPhoto, submitQuote } from "../lib/apiClient";
import { VEHICLE_TYPES, VEHICLE_SIZES } from "../data";

const SERVICES = ["Ceramic Coating", "Paint Correction", "Full Detail", "Interior Detail", "Exterior Detail", "Not sure yet"];

export default function QuoteModal({ open, onOpenChange, presetService }) {
  const [form, setForm] = useState({
    service_interest: "Ceramic Coating", vehicle_type: "car", vehicle_size: VEHICLE_SIZES[1],
    condition_notes: "", name: "", email: "", phone: "",
  });
  const [photos, setPhotos] = useState([]); // {path, preview}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (presetService) setForm((f) => ({ ...f, service_interest: presetService }));
  }, [presetService]);

  useEffect(() => {
    if (!open) setTimeout(() => { setDone(false); setPhotos([]); }, 300);
  }, [open]);

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, 8);
    setUploading(true);
    for (const file of list) {
      try {
        const { path } = await uploadPhoto(file);
        setPhotos((p) => [...p, { path, preview: URL.createObjectURL(file) }]);
      } catch {
        toast.error(`Couldn't upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !form.condition_notes) {
      toast.error("Please fill in your details and condition notes.");
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote({ ...form, photo_paths: photos.map((p) => p.path) });
      setDone(true);
    } catch {
      toast.error("Could not submit. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-modal"
        className="max-w-2xl bg-[#080808] border border-zinc-800 rounded-none p-0 gap-0 max-h-[92vh] overflow-y-auto text-white"
      >
        <DialogTitle className="sr-only">Get a coating quote</DialogTitle>
        {done ? (
          <div className="p-10 text-center" data-testid="quote-success">
            <div className="w-14 h-14 border border-[#FF3B30] flex items-center justify-center mx-auto mb-6">
              <Check className="text-[#FF3B30]" size={26} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">Request received</h3>
            <p className="text-zinc-400 mt-3 max-w-md mx-auto">
              Thanks, {form.name.split(" ")[0] || "there"}. We'll review your photos and details and send a personalised quote shortly. Check your inbox for a confirmation.
            </p>
            <Button onClick={() => onOpenChange(false)} data-testid="quote-close" className="mt-7 rounded-none bg-white text-black hover:bg-zinc-200 font-display uppercase tracking-widest">
              Done
            </Button>
          </div>
        ) : (
          <div className="p-7 md:p-9">
            <p className="text-[11px] tracking-[0.25em] text-[#FF3B30] uppercase">Get a Coating Quote</p>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-1 mb-7">
              Tell us about the job
            </h3>

            <div className="grid gap-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs tracking-widest uppercase">Service</Label>
                  <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                    <SelectTrigger data-testid="quote-service" className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-zinc-800 text-white rounded-none">
                      {SERVICES.map((s) => <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs tracking-widest uppercase">Vehicle size</Label>
                  <Select value={form.vehicle_size} onValueChange={(v) => setForm({ ...form, vehicle_size: v })}>
                    <SelectTrigger data-testid="quote-size" className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-zinc-800 text-white rounded-none">
                      {VEHICLE_SIZES.map((s) => <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs tracking-widest uppercase">Vehicle type</Label>
                <div className="grid grid-cols-3 gap-px bg-zinc-800 border border-zinc-800 mt-2">
                  {VEHICLE_TYPES.map((v) => (
                    <button
                      key={v.key}
                      data-testid={`quote-vehicle-${v.key}`}
                      onClick={() => setForm({ ...form, vehicle_type: v.key })}
                      className={`bg-[#0A0A0A] py-3 font-display uppercase text-sm tracking-wide transition-colors duration-300 ${form.vehicle_type === v.key ? "bg-[#FF3B30] text-white" : "hover:bg-[#111]"}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-xs tracking-widest uppercase">Condition & goals</Label>
                <Textarea
                  data-testid="quote-notes"
                  value={form.condition_notes}
                  onChange={(e) => setForm({ ...form, condition_notes: e.target.value })}
                  placeholder="Swirls, scratches, faded paint, water spots… what would you like the finish to look like?"
                  className="mt-2 bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white resize-none min-h-[90px]"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-xs tracking-widest uppercase">Photos <span className="text-zinc-600 normal-case tracking-normal">(optional, but gets you an accurate quote)</span></Label>
                <label
                  data-testid="quote-dropzone"
                  className="mt-2 flex flex-col items-center justify-center border border-dashed border-zinc-700 bg-[#0A0A0A] py-8 cursor-pointer hover:border-[#FF3B30] transition-colors"
                >
                  <Camera size={26} strokeWidth={1.5} className="text-zinc-500 mb-2" />
                  <span className="text-zinc-400 text-sm">{uploading ? "Uploading…" : "Tap to add vehicle photos"}</span>
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => handleFiles(e.target.files)} data-testid="quote-file-input" />
                </label>
                {photos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-3">
                    {photos.map((p, i) => (
                      <div key={i} className="relative aspect-square overflow-hidden border border-zinc-800">
                        <img src={p.preview} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 bg-black/70 p-0.5"
                          data-testid={`quote-remove-photo-${i}`}
                        ><X size={14} className="text-white" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <Input data-testid="quote-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white" />
                <Input data-testid="quote-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white" />
                <Input data-testid="quote-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-zinc-800 rounded-none focus:border-white text-white" />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="quote-submit"
                className="w-full mt-2 rounded-none bg-[#FF3B30] hover:bg-[#D63026] text-white h-12 font-display uppercase tracking-widest"
              >
                {submitting ? <Loader2 className="animate-spin" size={18} /> : "Request my quote"}
              </Button>
              <p className="text-center text-zinc-600 text-xs">No deposit until we send you a price. We'll email you a quote.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
